import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-modal';
import { useTranslation } from 'react-i18next';
import style from '../../style/components/signin/AccountDeleteModal.module.scss';
import StyledModal from '../StyledModal';
import LabelSelect from '../LabelSelect';
import LabelTextArea from '../LabelTextArea';
import IndicatesRequiredField from '../IndicatesRequiredField';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCloseAccountReasons, closeAccountConfirm } from '../../actions';
import AccountDeleteConfirmModal from './AccountDeleteConfirmModal';
import { isEmpty } from '../../utils/isEmpty';

if (process.env.NODE_ENV !== 'test') {
  Modal.setAppElement('#root');
}

function initialFormData() {
  return {
    reason: '',
    comment: '',
  };
}

function isCommentRequired(formData, reasons) {
  const otherReason = reasons.find((r) => r.name === 'Other');
  return otherReason && formData.reason === otherReason.id.toString();
}

function validate(formData, reasons, t) {
  const errors = {};

  if (isEmpty(formData.reason)) {
    errors.reason = t('account.delete.error_reasonRequired');
  }

  if (isCommentRequired(formData, reasons) && isEmpty(formData.comment)) {
    errors.comment = t('account.delete.error_commentRequired');
  }

  return errors;
}

const AccountDeleteModal = ({ closeModal, isOpen }) => {
  const { t } = useTranslation();

  const [isAccountDeleteConfirmModalOpen, setIsAccountDeleteConfirmModalOpen] =
    useState(false);

  const [errors, setErrors] = useState(false);
  const [formData, setFormData] = useState(initialFormData());
  const handleChange = (event) => {
    setFormData({ ...formData, [event.target.name]: event.target.value });
  };
  const dispatch = useDispatch();
  const account = useSelector((state) => state.account);

  useEffect(() => {
    // fetch close account reasons when modal is opened
    if (isOpen && !account.isFetching && !account.reasons.length) {
      dispatch(fetchCloseAccountReasons());
    }

    // close modal if the account has been deleted
    if (isOpen && !account.isFetching && account.deleted) {
      setIsAccountDeleteConfirmModalOpen(true);
      closeModal();
    }
  }, [
    isOpen,
    closeModal,
    dispatch,
    account,
    setIsAccountDeleteConfirmModalOpen,
  ]);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      const newErrors = validate(formData, account.reasons, t);
      setErrors(newErrors);

      if (!Object.keys(newErrors).length) {
        dispatch(
          closeAccountConfirm({
            comment: formData.comment,
            id: formData.reason,
          }),
        );
      }
    },
    [dispatch, formData, setErrors, account.reasons, t],
  );

  const submitting = account.isFetching;

  return (
    <>
      <StyledModal
        isOpen={isOpen}
        closeModal={() => {
          setFormData(initialFormData());
          setErrors(false);
          closeModal();
        }}
        submitting={submitting}
        title={t('account.delete.title')}
        wrapper={(children) => (
          <form onSubmit={handleSubmit} noValidate>
            {children}
          </form>
        )}
        primaryButton={{ text: t('account.delete.button_confirm') }}
        cancelButton={{ text: t('account.delete.button_cancel') }}
      >
        <p>{t('account.delete.description_feedback')}</p>

        <IndicatesRequiredField />
        <div className={style.inputGroup}>
          <div className={style.fullWidth}>
            <LabelSelect
              name="reason"
              label={t('account.delete.label_reason')}
              placeholder={t('account.delete.placeholder_reason')}
              error={errors.reason}
              value={formData.reason}
              onChange={handleChange}
              options={account.reasons.map((r) => ({
                value: r.id,
                label: r.name,
              }))}
              disabled={submitting || null}
              noMargin
              required
            />
          </div>
        </div>

        <div className={style.inputGroup}>
          <div className={style.fullWidth}>
            <LabelTextArea
              name="comment"
              label={t('account.delete.label_comment')}
              placeholder={t('account.delete.placeholder_comment')}
              error={errors.comment}
              value={formData.comment}
              onChange={handleChange}
              required={isCommentRequired(formData, account.reasons)}
            />
          </div>
        </div>
      </StyledModal>
      <AccountDeleteConfirmModal
        isOpen={isAccountDeleteConfirmModalOpen}
        closeModal={() => setIsAccountDeleteConfirmModalOpen(false)}
      />
    </>
  );
};

AccountDeleteModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
};

AccountDeleteModal.defaultProps = {
  reasons: [],
};

export default AccountDeleteModal;
